import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Heart, Anchor, ShieldCheck, Activity, Sun, MessagesSquare, Cross } from "lucide-react";
import BottomNav from "@/components/site/BottomNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  Peace: { Icon: Sun, color: "#7B9DB8" },
  Faith: { Icon: Anchor, color: "#1B3A5C" },
  Strength: { Icon: ShieldCheck, color: "#244E78" },
  Healing: { Icon: Activity, color: "#B8862A" },
  Hope: { Icon: Sun, color: "#C9A24A" },
  Prayer: { Icon: MessagesSquare, color: "#11294B" },
  Salvation: { Icon: Cross, color: "#0B1F3A" },
};

const ALL = "All";

export default function BiblePromises() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(ALL);

  useEffect(() => {
    axios.get(`${API}/bible-promises`).then((r) => setItems(r.data || [])).catch(() => {});
  }, []);

  const categories = useMemo(() => {
    const set = new Set(items.map((i) => i.category));
    return [ALL, ...Array.from(set)];
  }, [items]);

  const filtered = active === ALL ? items : items.filter((i) => i.category === active);

  return (
    <div data-testid="bible-promises-page">
      {/* HERO — gentle radiance */}
      <section className="page-hero" style={{ backgroundColor: "var(--gold-600)" }}>
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, var(--navy-900) 0%, var(--navy-700) 45%, var(--gold-500) 100%)",
          }}
        />
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-20 lg:py-28 text-center">
          <Heart className="text-gold-light mx-auto mb-5" size={40} strokeWidth={1.4} />
          <div className="text-xs uppercase tracking-[0.32em] text-gold-light mb-3">Bible Promises</div>
          <h1 className="font-display text-4xl lg:text-6xl text-cream leading-tight">
            God has spoken. His Word holds.
          </h1>
          <p className="mt-5 text-cream/85 text-lg max-w-2xl mx-auto leading-relaxed">
            For every fear, sorrow, and longing — Scripture has a promise. Hide them in your heart and let them carry you home.
          </p>
        </div>
      </section>

      {/* CATEGORY FILTER */}
      <section className="py-10 bg-white border-b border-cream-200 sticky top-20 z-30">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center" data-testid="promises-filters">
            {categories.map((cat) => (
              <button
                key={cat}
                data-testid={`filter-${cat.toLowerCase()}`}
                onClick={() => setActive(cat)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  active === cat
                    ? "btn-primary"
                    : "bg-cream-50 text-navy border border-cream-200 hover:border-gold"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* PROMISE CARDS — diverse mosaic */}
      <section className="py-16 lg:py-20 paper-texture">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p, i) => {
              const meta = CATEGORY_META[p.category] || { Icon: Heart, color: "var(--navy-700)" };
              const Icon = meta.Icon;
              const tall = i % 5 === 2;
              return (
                <article
                  key={`${p.category}-${i}`}
                  data-testid={`promise-card-${i}`}
                  className={`lift relative bg-white rounded-2xl p-7 border border-cream-200 hover:border-gold flex flex-col ${
                    tall ? "lg:row-span-2" : ""
                  }`}
                >
                  <div
                    className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl"
                    style={{ backgroundColor: meta.color }}
                  />
                  <div className="flex items-center justify-between mb-5">
                    <span
                      className="px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider"
                      style={{ backgroundColor: "var(--cream-100)", color: meta.color }}
                    >
                      {p.category}
                    </span>
                    <Icon size={22} style={{ color: meta.color }} />
                  </div>
                  <blockquote className="scripture text-navy-deep text-lg lg:text-xl leading-relaxed flex-1">
                    "{p.verse_text}"
                  </blockquote>
                  <p className="mt-4 text-gold font-semibold tracking-wider text-sm">— {p.verse_ref}</p>
                </article>
              );
            })}
          </div>
          {filtered.length === 0 && (
            <p className="text-center text-navy/60 py-12">No promises in this category yet.</p>
          )}
        </div>
      </section>

      {/* CLOSING SCRIPTURE BANNER */}
      <section className="py-16 lg:py-20" style={{ backgroundColor: "var(--navy-900)" }}>
        <div className="max-w-3xl mx-auto px-5 text-center text-cream">
          <p className="scripture text-2xl lg:text-3xl leading-relaxed">
            "For all the promises of God in him are yea, and in him Amen, unto the glory of God by us."
          </p>
          <p className="mt-4 text-gold-light text-xs tracking-[0.3em] uppercase">2 Corinthians 1:20</p>
        </div>
      </section>

      <BottomNav exclude={["/bible-promises"]} />
    </div>
  );
}
